/**
 * Buckets (design board 3; the sandbox's S3): one bucket's folders and files as tiles or a list; a file opens beside
 * them with its preview (images and PDFs fetched as bytes with the person's token), its details and actions: download,
 * a link, replace, rename or move, delete. Files upload with the picker or by dropping them on the list.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import DataTable, { sortTableRows } from '../table/DataTable';
import { CheckSquare, DownloadSimple, File, FilePdf, FileText, FolderSimple, Link as LinkIcon, ListBullets, SquaresFour, UploadSimple } from '@phosphor-icons/react';
import { ConfirmDialog, DataDialog, EmptyState, ErrorState, Loading, ReadOnlyBadge, errorText, saveBlob } from './DataCommon';
import { deleteObject, getBucketZip, getObjectBlob, listObjects, moveObject, objectLink, objectUsedIn, putObject } from '../../services/DataService';
import { ago, baseName, fileKind, formatBytes } from '../../utils/dataModel';

const THUMB_MAX_BYTES = 1.5 * 1024 * 1024;
const THUMB_COUNT = 24;
const TEXT_MAX_BYTES = 200 * 1024;
const readText = (blob) => (typeof blob.text === 'function' ? blob.text() : new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result || ''));
  reader.onerror = () => reject(reader.error);
  reader.readAsText(blob);
}));
const canBlobUrl = () => typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function';

/** An object's bytes as a blob URL while shown (revoked after). */
function useObjectUrl(projectId, bucket, object, enabled = true) {
  const [state, setState] = useState({ url: null, text: null, error: null, loading: false });
  useEffect(() => {
    if (!enabled || !object) { setState({ url: null, text: null, error: null, loading: false }); return undefined; }
    let live = true;
    let url = null;
    setState({ url: null, text: null, error: null, loading: true });
    getObjectBlob(projectId, bucket, object.key)
      .then(async (blob) => {
        if (!live) { return; }
        const kind = fileKind(object.key, object.contentType || blob.type);
        if (kind === 'text') {
          const text = await readText(blob);
          if (live) { setState({ url: null, text, error: null, loading: false }); }
          return;
        }
        url = canBlobUrl() ? URL.createObjectURL(blob) : null;
        setState({ url, text: null, error: null, loading: false });
      })
      .catch((e) => { if (live) { setState({ url: null, text: null, error: errorText(e), loading: false }); } });
    return () => {
      live = false;
      if (url) { URL.revokeObjectURL(url); }
    };
  }, [projectId, bucket, object?.key, enabled]); // eslint-disable-line react-hooks/exhaustive-deps
  return state;
}

function Thumb({ projectId, bucket, object, load }) {
  const kind = fileKind(object.key, object.contentType);
  const { url } = useObjectUrl(projectId, bucket, object, load && kind === 'image');
  if (url) { return <div className="data-thumb"><img alt="" src={url} /></div>; }
  const Icon = kind === 'pdf' ? FilePdf : kind === 'text' ? FileText : File;
  const ext = (object.key.split('.').pop() || '').toUpperCase();
  return <div className="data-thumb"><Icon aria-hidden size={22} /><span>{ext.length <= 5 ? ext : ''}</span></div>;
}

Thumb.propTypes = { bucket: PropTypes.string, load: PropTypes.bool, object: PropTypes.object, projectId: PropTypes.string };

function ObjectPreview({ projectId, bucket, object }) {
  const kind = fileKind(object.key, object.contentType);
  const previewable = kind === 'image' || kind === 'pdf' || (kind === 'text' && Number(object.size || 0) <= TEXT_MAX_BYTES);
  const { url, text, error, loading } = useObjectUrl(projectId, bucket, object, previewable);
  if (!previewable) { return <div className="data-thumb is-large"><File aria-hidden size={28} /><span>No preview for this file</span></div>; }
  if (loading) { return <div className="data-thumb is-large"><Loading label="Loading the preview…" /></div>; }
  if (error) { return <div className="data-thumb is-large"><span>{error}</span></div>; }
  if (kind === 'image' && url) { return <div className="data-thumb is-large"><img alt={`Preview of ${baseName(object.key)}`} src={url} /></div>; }
  if (kind === 'pdf' && url) { return <iframe className="data-pdf" src={url} title={`Preview of ${baseName(object.key)}`} />; }
  if (text !== null) { return <pre className="data-text-preview data-mono">{text}</pre>; }
  return <div className="data-thumb is-large"><span>No preview</span></div>;
}

ObjectPreview.propTypes = { bucket: PropTypes.string, object: PropTypes.object, projectId: PropTypes.string };

/** "products · id 1182" / "reviews · 65f1c2…a9": where a reference points. */
export function refLabel(ref) {
  if (ref.store === 'postgres') {
    const key = Object.entries(ref.key || {}).map(([k, v]) => `${k} ${v}`).join(', ');
    return `${ref.schema && ref.schema !== 'public' ? `${ref.schema}.` : ''}${ref.table} · ${key}`;
  }
  const id = ref._id && typeof ref._id === 'object' && ref._id.$oid ? ref._id.$oid : JSON.stringify(ref._id);
  return `${ref.collection} · ${String(id).length > 14 ? `${String(id).slice(0, 6)}…${String(id).slice(-2)}` : id}`;
}

/** The rows and documents that mention the object's key; nothing when there are none (or the lookup failed). */
function UsedIn({ projectId, bucket, objectKey, onOpenRef }) {
  const [refs, setRefs] = useState(null);
  useEffect(() => {
    let live = true;
    setRefs(null);
    // Not on every click through the list: a short pause first.
    const timer = setTimeout(() => {
      Promise.resolve().then(() => objectUsedIn(projectId, bucket, objectKey))
        .then((answer) => { if (live) { setRefs(answer?.refs || []); } })
        .catch(() => { if (live) { setRefs([]); } });
    }, 250);
    return () => { live = false; clearTimeout(timer); };
  }, [projectId, bucket, objectKey]);
  if (!refs?.length) { return null; }
  return (
    <>
      <dt>Used in</dt>
      <dd data-testid="used-in">
        {refs.map((ref, i) => (
          <React.Fragment key={`${refLabel(ref)}-${ref.column || ref.field}-${i}`}>
            {i > 0 && <br />}
            {onOpenRef
              ? <button className="data-link" onClick={() => onOpenRef(ref)} type="button">{refLabel(ref)}</button>
              : refLabel(ref)}
            {(ref.column || ref.field) && <span className="data-muted"> ({ref.column || ref.field})</span>}
          </React.Fragment>
        ))}
      </dd>
    </>
  );
}

UsedIn.propTypes = { bucket: PropTypes.string, objectKey: PropTypes.string, onOpenRef: PropTypes.func, projectId: PropTypes.string };

const LIST_SORT_VALUES = {
  name: (row) => baseName(row.key),
  size: (row) => row.object?.size,
  type: (row) => (row.folder ? 'Folder' : row.object.contentType || ''),
  modified: (row) => (row.object ? Date.parse(row.object.lastModified) || null : null),
};

/** The list view's rows: the folders first (by name when sorting on it), then the files in the chosen order. */
function listRows(folders, objects, sort) {
  const folderRows = folders.map((folder) => ({ key: folder, folder: true }));
  const fileRows = objects.map((object) => ({ key: object.key, object }));
  const column = sort.key ? { value: LIST_SORT_VALUES[sort.key] } : null;
  return [
    ...(sort.key === 'name' ? sortTableRows(folderRows, column, sort.dir) : folderRows),
    ...sortTableRows(fileRows, column, sort.dir),
  ];
}

export default function BucketsView({ projectId, store, bucket, readOnly = false, onOpenRef = null, onApplied = null }) {
  const [prefix, setPrefix] = useState('');
  const [answer, setAnswer] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [find, setFind] = useState('');
  const [layout, setLayout] = useState('grid');
  const [selected, setSelected] = useState(null);
  const [uploads, setUploads] = useState([]); // [{ name, state: 'sending'|'done'|'failed', error }]
  const [dragging, setDragging] = useState(false);
  const [dialog, setDialog] = useState(null); // 'rename' | 'delete' | 'folder'
  const [dialogText, setDialogText] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [link, setLink] = useState(null);
  const [picking, setPicking] = useState(false);
  const [picked, setPicked] = useState(() => new Set()); // keys of files and prefixes of folders (they end in "/")
  const [zipping, setZipping] = useState(false);
  const [listSort, setListSort] = useState({ key: null, dir: 'asc' });
  const pickRef = useRef(null);
  const replaceRef = useRef(null);
  const canWrite = !readOnly && !store.readOnly;

  const load = useCallback(async (token) => {
    setLoading(true);
    setError(null);
    try {
      const result = await listObjects(projectId, bucket, { prefix, token });
      setAnswer((prev) => (token && prev ? { ...result, folders: [...prev.folders, ...(result.folders || [])], objects: [...prev.objects, ...(result.objects || [])] } : { folders: result.folders || [], objects: result.objects || [], nextToken: result.nextToken }));
    } catch (e) {
      setError(errorText(e));
    } finally {
      setLoading(false);
    }
  }, [projectId, bucket, prefix]);

  useEffect(() => { load(); }, [load]);

  const go = (next) => { setPrefix(next); setSelected(null); setLink(null); setFind(''); setPicked(new Set()); };
  const togglePicked = (path) => setPicked((prev) => {
    const next = new Set(prev);
    if (next.has(path)) { next.delete(path); } else { next.add(path); }
    return next;
  });
  const stopPicking = () => { setPicking(false); setPicked(new Set()); };

  // A zip of what is picked, of this folder (nothing picked, `paths` empty) or of the whole bucket (at its root).
  const downloadZip = async (paths, name) => {
    setActionError(null);
    setZipping(true);
    try {
      const blob = await getBucketZip(projectId, bucket, { base: prefix, paths });
      saveBlob(blob, `${name.replace(/\/$/, '')}.zip`);
    } catch (e) {
      setActionError(errorText(e));
    } finally {
      setZipping(false);
    }
  };

  const upload = async (files) => {
    const list = [...(files || [])];
    if (!list.length || !canWrite) { return; }
    setUploads(list.map((f) => ({ name: f.name, state: 'sending' })));
    const results = await Promise.all(list.map((file) => putObject(projectId, bucket, `${prefix}${file.name}`, file, file.type)
      .then(() => ({ name: file.name, state: 'done' }))
      .catch((e) => ({ name: file.name, state: 'failed', error: errorText(e) }))));
    setUploads(results);
    load();
  };

  const onDrop = (event) => {
    event.preventDefault();
    setDragging(false);
    upload(event.dataTransfer?.files);
  };

  const download = async (object) => {
    setActionError(null);
    try {
      const blob = await getObjectBlob(projectId, bucket, object.key, { download: true });
      saveBlob(blob, baseName(object.key));
    } catch (e) {
      setActionError(errorText(e));
    }
  };

  const copyLink = async (object) => {
    setActionError(null);
    try {
      const result = await objectLink(projectId, bucket, object.key, 3600);
      const absolute = result.url && !/^https?:/i.test(result.url) ? `${window.location.origin}${result.url}` : result.url;
      let copied = false;
      try { await navigator.clipboard.writeText(absolute); copied = true; } catch { /* shown below to copy by hand */ }
      setLink({ url: absolute, copied, note: result.note || null, expiresAt: result.expiresAt || null, proxied: Boolean(result.url && !/^https?:/i.test(result.url)) });
    } catch (e) {
      setActionError(errorText(e));
    }
  };

  const replace = async (files) => {
    const file = files?.[0];
    if (!file || !selected) { return; }
    setActionError(null);
    try {
      await putObject(projectId, bucket, selected.key, file, file.type || selected.contentType);
      setSelected({ ...selected, size: file.size, contentType: file.type || selected.contentType, lastModified: new Date().toISOString() });
      load();
    } catch (e) {
      setActionError(errorText(e));
    }
  };

  const runDialog = async () => {
    setBusy(true);
    setActionError(null);
    try {
      if (dialog === 'delete') {
        const answer = await deleteObject(projectId, bucket, selected.key);
        if (answer?.restorePoint || answer?.restorePointWarning) { onApplied?.({ ...answer, summary: `Deleted ${selected.key}` }); }
        setSelected(null);
      } else if (dialog === 'rename') {
        const to = dialogText.trim().replace(/^\/+/, '');
        if (!to || to === selected.key) { setDialog(null); setBusy(false); return; }
        const answer = await moveObject(projectId, bucket, selected.key, to);
        if (answer?.restorePoint || answer?.restorePointWarning) { onApplied?.({ ...answer, summary: `Moved ${selected.key} to ${to}` }); }
        setSelected({ ...selected, key: to });
      } else if (dialog === 'folder') {
        const name = dialogText.trim().replace(/^\/+|\/+$/g, '');
        if (name) { go(`${prefix}${name}/`); }
        setDialog(null);
        setBusy(false);
        return;
      }
      setDialog(null);
      load();
    } catch (e) {
      setActionError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const crumbs = prefix.split('/').filter(Boolean);
  const needle = find.trim().toLowerCase();
  const folders = (answer?.folders || []).filter((f) => !needle || baseName(f).toLowerCase().includes(needle));
  const objects = (answer?.objects || []).filter((o) => o.key !== prefix && (!needle || baseName(o.key).toLowerCase().includes(needle)));
  let imageIndex = 0;

  return (
    <div className="data-view" data-testid="buckets-view">
      <div className="data-view-head">
        <h1>{bucket}</h1>
        <nav aria-label="Folder" className="data-crumbs data-mono">
          <button className="data-link" onClick={() => go('')} type="button">{bucket}</button>
          {crumbs.map((part, index) => {
            const to = `${crumbs.slice(0, index + 1).join('/')}/`;
            return (
              <React.Fragment key={to}>
                {' / '}
                {index === crumbs.length - 1 ? <span aria-current="location">{part}</span> : <button className="data-link" onClick={() => go(to)} type="button">{part}</button>}
              </React.Fragment>
            );
          })}
        </nav>
        {!canWrite && <ReadOnlyBadge reason={readOnly ? 'You are a viewer of this project' : 'This store is read-only here'} />}
        <span className="data-grow" />
        <button className="data-btn" disabled={zipping} onClick={() => downloadZip([], crumbs.length ? baseName(prefix) : bucket)} type="button">
          <DownloadSimple aria-hidden size={15} />{zipping ? 'Preparing zip…' : crumbs.length ? 'Download folder' : 'Download bucket'}
        </button>
        {canWrite && (
          <>
            <button className="data-btn" onClick={() => { setDialogText(''); setDialog('folder'); }} type="button">New folder</button>
            <button className="data-btn is-primary" onClick={() => pickRef.current?.click()} type="button"><UploadSimple aria-hidden size={15} />Upload files</button>
            <input aria-label="Files to upload" className="data-sr" data-testid="upload-input" multiple onChange={(e) => { upload(e.target.files); e.target.value = ''; }} ref={pickRef} tabIndex={-1} type="file" />
          </>
        )}
      </div>

      <div className="data-split">
        <div
          className={`data-card data-objects${dragging ? ' is-dragging' : ''}`}
          data-testid="bucket-drop"
          onDragLeave={(e) => { if (e.currentTarget === e.target) { setDragging(false); } }}
          onDragOver={(e) => { if (canWrite) { e.preventDefault(); setDragging(true); } }}
          onDrop={canWrite ? onDrop : undefined}
        >
          <div className="data-card-head">
            <label className="data-label" htmlFor="data-prefix">Find</label>
            <input className="data-input is-wide" id="data-prefix" onChange={(e) => setFind(e.target.value)} placeholder="Name in this folder" value={find} />
            <button aria-pressed={picking} className="data-btn" onClick={() => (picking ? stopPicking() : setPicking(true))} type="button"><CheckSquare aria-hidden size={14} />Select</button>
            <div aria-label="Layout" className="data-seg" role="group">
              <button aria-pressed={layout === 'grid'} onClick={() => setLayout('grid')} type="button"><SquaresFour aria-hidden size={14} />Grid</button>
              <button aria-pressed={layout === 'list'} onClick={() => setLayout('list')} type="button"><ListBullets aria-hidden size={14} />List</button>
            </div>
          </div>

          {picking && (
            <div className="data-pickbar" role="status">
              <strong>{picked.size} selected</strong>
              <button className="data-link" onClick={() => setPicked(new Set([...folders, ...objects.map((o) => o.key)]))} type="button">Select all here</button>
              <button className="data-link" disabled={picked.size === 0} onClick={() => setPicked(new Set())} type="button">Clear</button>
              <span className="data-grow" />
              <button className="data-btn is-primary" disabled={picked.size === 0 || zipping} onClick={() => downloadZip([...picked], picked.size === 1 ? baseName([...picked][0]) : (crumbs.length ? baseName(prefix) : bucket))} type="button">
                <DownloadSimple aria-hidden size={14} />{zipping ? 'Preparing zip…' : 'Download selected (zip)'}
              </button>
            </div>
          )}
          {error && <ErrorState error={error} onRetry={() => load()} />}
          {!error && !answer && loading && <Loading label="Loading the files…" />}
          {answer && !folders.length && !objects.length && (
            <EmptyState title={needle ? 'Nothing matches' : prefix ? 'This folder is empty' : 'This bucket is empty'}>
              {canWrite && !needle ? 'Upload files, or drop them here.' : null}
            </EmptyState>
          )}
          {answer && (folders.length > 0 || objects.length > 0) && (layout === 'grid' ? (
            <ul aria-label="Files" className="data-tiles">
              {folders.map((folder) => (
                <li key={folder}>
                  <button aria-pressed={picking ? picked.has(folder) : undefined} className={`data-tile is-folder${picked.has(folder) ? ' is-on' : ''}`} onClick={() => (picking ? togglePicked(folder) : go(folder))} type="button">
                    {picking && <input aria-label={`Select ${baseName(folder)}`} checked={picked.has(folder)} className="data-pick" onChange={() => togglePicked(folder)} onClick={(e) => e.stopPropagation()} type="checkbox" />}
                    <div className="data-thumb"><FolderSimple aria-hidden size={24} /><span>Folder</span></div>
                    <div className="data-tile-meta"><strong>{baseName(folder)}</strong></div>
                  </button>
                </li>
              ))}
              {objects.map((object) => {
                const isImage = fileKind(object.key, object.contentType) === 'image';
                const loadThumb = isImage && Number(object.size || 0) <= THUMB_MAX_BYTES && (imageIndex += 1) <= THUMB_COUNT;
                const on = picking ? picked.has(object.key) : selected?.key === object.key;
                return (
                  <li key={object.key}>
                    <button aria-pressed={on} className={`data-tile${on ? ' is-on' : ''}`} data-testid="object-tile" onClick={() => { if (picking) { togglePicked(object.key); } else { setSelected(object); setLink(null); } }} type="button">
                      {picking && <input aria-label={`Select ${baseName(object.key)}`} checked={picked.has(object.key)} className="data-pick" onChange={() => togglePicked(object.key)} onClick={(e) => e.stopPropagation()} type="checkbox" />}
                      <Thumb bucket={bucket} load={loadThumb} object={object} projectId={projectId} />
                      <div className="data-tile-meta"><strong>{baseName(object.key)}</strong>{formatBytes(object.size)}{object.lastModified ? ` · ${ago(object.lastModified)}` : ''}</div>
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="data-table-wrap">
              <DataTable
                className="data-table"
                columns={[
                  {
                    key: 'name',
                    label: 'Name',
                    value: (row) => baseName(row.key),
                    title: (row) => row.key,
                    render: (row) => (row.folder
                      ? <button className="data-link" onClick={() => go(row.key)} type="button"><FolderSimple aria-hidden size={14} /> {baseName(row.key)}</button>
                      : <button className="data-link" onClick={() => { setSelected(row.object); setLink(null); }} type="button">{baseName(row.key)}</button>),
                  },
                  { key: 'size', label: 'Size', width: 110, className: 'data-mono', value: (row) => (row.folder ? null : row.object.size), render: (row) => (row.folder ? '' : formatBytes(row.object.size)) },
                  { key: 'type', label: 'Type', width: 180, className: 'data-mono', value: (row) => (row.folder ? 'Folder' : row.object.contentType || '') },
                  { key: 'modified', label: 'Modified', width: 130, value: (row) => (row.folder ? null : Date.parse(row.object.lastModified) || row.object.lastModified), title: (row) => (row.folder ? undefined : String(row.object.lastModified || '')), render: (row) => (row.folder ? '' : ago(row.object.lastModified)) },
                ]}
                leading={picking ? {
                  width: 40,
                  header: <span className="data-sr">Select</span>,
                  render: (row) => <input aria-label={`Select ${baseName(row.key)}`} checked={picked.has(row.key)} onChange={() => togglePicked(row.key)} type="checkbox" />,
                } : undefined}
                onSort={setListSort}
                rowKey="key"
                rowProps={(row) => ({ className: !row.folder && selected?.key === row.key ? 'is-selected' : '' })}
                rows={listRows(folders, objects, listSort)}
                serverSort
                sort={listSort}
                storageKey="flow.data.bucket.hiddenColumns"
              />
            </div>
          ))}
          {answer?.nextToken && (
            <div className="data-table-foot"><span /><button className="data-btn" disabled={loading} onClick={() => load(answer.nextToken)} type="button">Load more</button></div>
          )}
          {canWrite && (
            <div className="data-dropzone">Drop files here to upload to <span className="data-mono">{prefix || `${bucket}/`}</span></div>
          )}
          {uploads.length > 0 && (
            <div role="status">
              <ul aria-label="Uploads" className="data-uploads">
                {uploads.map((u) => (
                  <li className={`is-${u.state}`} key={u.name}>
                    {u.name}: {u.state === 'sending' ? 'uploading…' : u.state === 'done' ? 'uploaded' : `failed (${u.error})`}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <section aria-labelledby="data-obj-title" className="data-card data-object-detail">
          {!selected ? (
            <EmptyState title="Pick a file">Its preview and details open here.</EmptyState>
          ) : (
            <>
              <h2 id="data-obj-title">{baseName(selected.key)}</h2>
              <ObjectPreview bucket={bucket} object={selected} projectId={projectId} />
              <dl className="data-dl">
                <dt>Key</dt><dd className="data-mono">{selected.key}</dd>
                <dt>Size</dt><dd>{formatBytes(selected.size)}</dd>
                {selected.contentType && <><dt>Type</dt><dd className="data-mono">{selected.contentType}</dd></>}
                {selected.lastModified && <><dt>Modified</dt><dd>{ago(selected.lastModified)}</dd></>}
                <UsedIn bucket={bucket} objectKey={selected.key} onOpenRef={onOpenRef} projectId={projectId} />
              </dl>
              <div className="data-actions">
                <button className="data-btn" onClick={() => download(selected)} type="button"><DownloadSimple aria-hidden size={15} />Download</button>
                <button className="data-btn" onClick={() => copyLink(selected)} type="button"><LinkIcon aria-hidden size={15} />Copy link (1 h)</button>
                {canWrite && (
                  <>
                    <button className="data-btn" onClick={() => replaceRef.current?.click()} type="button">Replace…</button>
                    <input aria-label="File to replace it with" className="data-sr" onChange={(e) => { replace(e.target.files); e.target.value = ''; }} ref={replaceRef} tabIndex={-1} type="file" />
                    <button className="data-btn" onClick={() => { setDialogText(selected.key); setDialog('rename'); }} type="button">Rename</button>
                    <button className="data-btn is-danger" onClick={() => setDialog('delete')} type="button">Delete…</button>
                  </>
                )}
              </div>
              {actionError && <p className="data-error-text" role="alert">{actionError}</p>}
              {link && (
                <div className="data-link-box" role="status">
                  <span>{link.copied ? 'Link copied.' : 'Copy this link:'}</span>
                  <input aria-label="Link to the file" className="data-input data-mono is-wide" onFocus={(e) => e.target.select()} readOnly value={link.url} />
                  <small>
                    {link.note || (link.proxied
                      ? 'It opens through Flow, for people signed in with access to this project.'
                      : 'The link opens for anyone who has it, for one hour.')}
                  </small>
                </div>
              )}
              <p className="data-footnote">The bucket is the sandbox&apos;s own S3: your services reach it at <span className="data-mono">127.0.0.1:4566</span>.</p>
            </>
          )}
        </section>
      </div>

      {dialog === 'delete' && selected && (
        <ConfirmDialog busy={busy} error={actionError} onClose={() => setDialog(null)} onConfirm={runDialog} title={`Delete ${baseName(selected.key)}?`}>
          <p>The file goes from <strong>{bucket}</strong> for good: files cannot be undone.</p>
        </ConfirmDialog>
      )}
      {(dialog === 'rename' || dialog === 'folder') && (
        <DataDialog
          footer={(
            <>
              <span className="data-grow" />
              <button className="data-btn" onClick={() => setDialog(null)} type="button">Cancel</button>
              <button className="data-btn is-primary" disabled={busy || !dialogText.trim()} onClick={runDialog} type="button">{dialog === 'rename' ? 'Rename' : 'Open folder'}</button>
            </>
          )}
          onClose={() => setDialog(null)}
          title={dialog === 'rename' ? 'Rename or move' : 'New folder'}
        >
          <form onSubmit={(e) => { e.preventDefault(); runDialog(); }}>
            <label className="data-label" htmlFor="data-dialog-text">{dialog === 'rename' ? 'New key (a / moves it to a folder)' : `Folder name in ${prefix || `${bucket}/`}`}</label>
            <input className="data-input data-mono is-wide" data-autofocus id="data-dialog-text" onChange={(e) => setDialogText(e.target.value)} value={dialogText} />
            {dialog === 'folder' && <p className="data-footnote">A folder is kept once a file is uploaded into it.</p>}
            {actionError && <p className="data-error-text" role="alert">{actionError}</p>}
          </form>
        </DataDialog>
      )}
    </div>
  );
}

BucketsView.propTypes = {
  onApplied: PropTypes.func,
  bucket: PropTypes.string.isRequired,
  onOpenRef: PropTypes.func,
  projectId: PropTypes.string.isRequired,
  readOnly: PropTypes.bool,
  store: PropTypes.object.isRequired,
};
